// Registered style module for the chat-bubble component.
// Covers user question bubble, assistant answer bubble, and markdown formatting inside answers.

const styleElement = document.createElement("dom-module");

styleElement.innerHTML = /*html*/`<template>
    <style>
    :host {
      display: block;
      width: 100%;
    }

    .chat-bubble__user-row {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 10px;
      width: fit-content;
      max-width: 989px;
      margin-left: auto;
      box-sizing: border-box;
    }

    .chat-bubble__user-text {
      box-sizing: border-box;
      width: fit-content;
      max-width: 626px;
      background: #fafafa;
      border-radius: 16px;
      padding: 12px;
      color: var(--icon-color-token-color-icon-default, #424242);
      font-family: Lato, sans-serif;
      font-style: normal;
      font-weight: 400;
      line-height: 26px;
      text-align: left;
      word-wrap: break-word;
      overflow-wrap: anywhere;
    }

    .chat-bubble__user-images {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-start;
      gap: 4px;
      width: 293px;
      height: 119px;
    }

    .chat-bubble__user-files {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
      width: 293px;
      max-width: 100%;
      opacity: 1;
      box-sizing: border-box;
    }

    .chat-bubble__attachment-preview {
      position: relative;
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      box-sizing: border-box;
      border-radius: 9px;
      opacity: 1;
      overflow: hidden;
      padding: 0;
      border: 0;
    }

    .chat-bubble__attachment-preview--image {
      width: 144.5px;
      height: 119px;
      justify-content: center;
    }

    .chat-bubble__attachment-preview--pdf {
      width: 212px;
      height: 60px;
      background: #f4f4f4;
      border-radius: 6px;
    }

    .chat-bubble__attachment-preview--image .chat-bubble__attachment-thumb {
      width: 144.5px;
      height: 119px;
      border-radius: 9px;
      object-fit: cover;
      flex-shrink: 0;
      display: block;
    }

    .chat-bubble__attachment-preview--pdf .chat-bubble__attachment-pdf-badge {
      position: absolute;
      top: 5px;
      left: 4.5px;
      width: 50px;
      height: 50px;
      border-radius: 8px;
      background: #e35151;
      opacity: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #ffffff;
      font-family: "Lato", sans-serif;
      font-size: 16px;
      font-weight: 700;
      font-style: normal;
      line-height: 22px;
      letter-spacing: 0;
    }

    .chat-bubble__attachment-preview--pdf .chat-bubble__attachment-pdf-info {
      position: absolute;
      top: 11px;
      left: 62.49px;
      width: 126.34px;
      height: 38px;
      opacity: 1;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      gap: 2px;
      min-width: 0;
      box-sizing: border-box;
    }

    .chat-bubble__attachment-preview--pdf .chat-bubble__attachment-pdf-name {
      display: block;
      width: 100%;
      height: 18px;
      margin: 0;
      padding: 0;
      opacity: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: left;
      box-sizing: border-box;
      color: #424242;
      font-family: "Lato", sans-serif;
      font-size: 12px;
      font-weight: 700;
      font-style: normal;
      line-height: 18px;
      letter-spacing: 0;
    }

    .chat-bubble__attachment-preview--pdf .chat-bubble__attachment-pdf-type {
      display: block;
      width: 100%;
      height: 18px;
      margin: 0;
      padding: 0;
      text-align: left;
      font-family: "Lato", sans-serif;
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: 0;
      color: #7f7f7f;
    }

    .chat-bubble__assistant {
      box-sizing: border-box;
      max-width: 949px;
      width: 100%;
      display: flex;
      flex-direction: column;
      border-radius: 16px;
      padding: 12px;
      color: var(--font-color-token-color-font-default, #424242);
      font-family: Lato, sans-serif;
      font-style: normal;
      font-weight: 400;
      line-height: 26px;
      text-align: left;
      word-wrap: break-word;
    }

    /* --- Markdown inside assistant bubble --- */

    .chat-bubble__assistant h1,
    .chat-bubble__assistant h2,
    .chat-bubble__assistant h3 {
      margin: 12px 0 8px;
      font-size: 1.1em;
      font-weight: 600;
      color: var(--font-color-token-color-font-default, #424242);
    }

    .chat-bubble__assistant h1:first-child,
    .chat-bubble__assistant h2:first-child,
    .chat-bubble__assistant h3:first-child {
      margin-top: 0;
    }

    .chat-bubble__assistant p {
      margin: 8px 0;
      line-height: 26px;
    }

    .chat-bubble__assistant p:first-child {
      margin-top: 0;
    }

    .chat-bubble__assistant p:last-child {
      margin-bottom: 0;
    }

    .chat-bubble__assistant ul,
    .chat-bubble__assistant ol {
      margin: 8px 0;
      padding-left: 24px;
      line-height: 26px;
    }

    .chat-bubble__assistant li {
      margin: 4px 0;
    }

    .chat-bubble__assistant code {
      background: #e8e8e8;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: "SF Mono", Monaco, Consolas, monospace;
      font-size: 0.9em;
    }

    .chat-bubble__assistant pre {
      background: #2d2d2d;
      color: #f8f8f2;
      padding: 12px;
      border-radius: 8px;
      overflow-x: auto;
      margin: 12px 0;
      font-size: 13px;
      line-height: 1.5;
    }

    .chat-bubble__assistant pre code {
      background: none;
      padding: 0;
      color: inherit;
      font-size: inherit;
    }

    .chat-bubble__assistant blockquote {
      border-left: 3px solid #3570d2;
      margin: 12px 0;
      padding-left: 12px;
      color: #666;
    }

    .chat-bubble__assistant a {
      color: #3570d2;
      text-decoration: none;
    }

    .chat-bubble__assistant a:hover {
      text-decoration: underline;
    }

    .chat-bubble__assistant strong {
      font-weight: 600;
    }
    </style>
</template>`;
styleElement.register("chat-bubble-style");
